"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { WifiOff, X } from "lucide-react";
import { usePathname } from "@/i18n/navigation";
import { dismissNetworkFailure, useNetworkFailure, useOnline, useSlowNetwork } from "@/shared/lib/network";
import { Button } from "@/shared/ui/buttons/button";
import { cn } from "@/shared/lib/utils";

const isChatPath = (p: string) => p === "/chat" || p.startsWith("/chat/") || p === "/messages";

export function NetworkHintBar({ inline = false }: { inline?: boolean }) {
  const t = useTranslations("Network");
  const online = useOnline();
  const failure = useNetworkFailure();
  const pathname = usePathname();
  const [slowDismissed, setSlowDismissed] = useState(false);
  const slow = useSlowNetwork();
  const kind = failure ? (failure.uncertain ? "uncertain" : "interrupted") : !online ? "offline" : slow && !slowDismissed ? "slow" : null;
  if ((!inline && isChatPath(pathname)) || !kind) return null;
  return (
    <div role="status" aria-live="polite" className={cn(
      "flex shrink-0 items-start gap-2 border-b border-border bg-muted px-3 py-2 text-sm text-foreground",
      !inline && "fixed inset-x-0 top-0 z-[90] [body:has([data-chat-dialog])_&]:hidden [body:has(.gl-chat-root[role=dialog])_&]:hidden",
    )}>
      <WifiOff aria-hidden className="mt-2 size-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1 self-center leading-snug">{t(kind)}</span>
      {kind !== "slow" && <Button type="button" variant="ghost" size="sm" className="min-h-10 shrink-0" onClick={() => window.location.reload()}>{t("reload")}</Button>}
      {(failure || kind === "slow") && <Button type="button" variant="ghost" size="icon" aria-label={t("dismiss")} className="size-10 shrink-0" onClick={() => { if (failure) dismissNetworkFailure(); else setSlowDismissed(true); }}><X aria-hidden className="size-4" /></Button>}
    </div>
  );
}
